import { getReportFooterText } from '../reportFooter';
import type { PersonLabels } from '@/hooks/usePersonLabels';
export interface ClearedIndividualsHeaderInfo {
  fromDate?: string | null;
  toDate?: string | null;
  individualName?: string | null;
  memberStatus?: string | null;
  totalValue?: number | null;
}

export interface ClearedIndividualsItem {
  individual?: string | null;
  clearedDate: string;
  transactionTypeLabel: string;
  ie?: string | null;
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  quantity?: number | null;
  totalLineCost?: number | null;
  date: string;
  user: string;
}

export interface ClearedIndividualsTemplateOptions {
  generatedAt: Date;
  header: ClearedIndividualsHeaderInfo;
  items: ClearedIndividualsItem[];
  personLabels?: PersonLabels;
  ieKitLabel?: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

interface ColumnDef {
  key: string;
  label: string;
  className?: string;
  render: (item: ClearedIndividualsItem) => string;
  hasData: (items: ClearedIndividualsItem[]) => boolean;
}

const allColumns: ColumnDef[] = [
  {
    key: 'individual',
    label: 'Individual',
    render: (item) => sanitizeText(item.individual),
    hasData: () => true,
  },
  {
    key: 'clearedDate',
    label: 'Cleared Date',
    render: (item) => sanitizeText(item.clearedDate),
    hasData: () => true,
  },
  {
    key: 'transactionType',
    label: 'Issue / Turn In / DX',
    render: (item) => sanitizeText(item.transactionTypeLabel),
    hasData: () => true,
  },
  {
    key: 'ie',
    label: 'IE Kit',
    render: (item) => sanitizeText(item.ie),
    hasData: (items) => items.some((i) => i.ie != null && i.ie.trim().length > 0),
  },
  {
    key: 'lin',
    label: 'LIN',
    render: (item) => sanitizeText(item.lin),
    hasData: (items) => items.some((i) => i.lin != null && i.lin.trim().length > 0),
  },
  {
    key: 'nsn',
    label: 'NSN',
    render: (item) => sanitizeText(item.nsn),
    hasData: (items) => items.some((i) => i.nsn != null && i.nsn.trim().length > 0),
  },
  {
    key: 'nomenclature',
    label: 'Nomenclature',
    render: (item) => sanitizeText(item.nomenclature === '—' ? null : item.nomenclature),
    hasData: (items) => items.some((i) => i.nomenclature !== '—' && i.nomenclature.trim().length > 0),
  },
  {
    key: 'qty',
    label: 'Qty',
    className: 'numeric',
    render: (item) =>
      sanitizeText(item.quantity != null ? String(item.quantity) : ''),
    hasData: (items) => items.some((i) => i.quantity != null),
  },
  {
    key: 'totalLineCost',
    label: 'Total Line Cost',
    className: 'numeric',
    render: (item) => formatCurrency(item.totalLineCost ?? null),
    hasData: (items) => items.some((i) => i.totalLineCost != null && Number.isFinite(i.totalLineCost)),
  },
  {
    key: 'date',
    label: 'Date',
    render: (item) => sanitizeText(item.date),
    hasData: () => true,
  },
  {
    key: 'user',
    label: 'User',
    render: (item) => sanitizeText(item.user),
    hasData: (items) => items.some((i) => i.user !== '—' && i.user.trim().length > 0),
  },
];

const buildItemsTable = (items: ClearedIndividualsItem[], personLabel = 'Individual', personLowerLabel = 'individual', ieKitLabel = 'IE Kit'): { headers: string; rows: string; colCount: number } => {
  const columns = allColumns.map((col) => {
    if (col.key === 'individual') return { ...col, label: personLabel };
    if (col.key === 'ie') return { ...col, label: ieKitLabel };
    return col;
  });
  const visibleColumns = columns.filter((col) => col.hasData(items));
  const colCount = visibleColumns.length;

  const headers = visibleColumns.map((col) => `<th>${col.label}</th>`).join('\n            ');

  if (items.length === 0) {
    return {
      headers,
      rows: `
        <tr>
          <td colspan="${colCount}" class="empty-row">
            No cleared ${personLowerLabel} transactions matched the selected filters.
          </td>
        </tr>
      `,
      colCount,
    };
  }

  const rows = items
    .map(
      (item) => `
        <tr>
          ${visibleColumns
            .map((col) => `<td${col.className ? ` class="${col.className}"` : ''}>${col.render(item)}</td>`)
            .join('\n          ')}
        </tr>
      `,
    )
    .join('');

  return { headers, rows, colCount };
};

export const buildClearedIndividualsReportHtml = ({
  generatedAt,
  header,
  items,
  personLabels,
  ieKitLabel = 'IE Kit',
}: ClearedIndividualsTemplateOptions): string => {
  const pl = personLabels ?? { label: 'Individual', labelPlural: 'Individuals', lower: 'individual', lowerPlural: 'individuals', article: 'an' as const };
  const generatedLabel = formatDateTime(generatedAt);
  const fromDateLabel = header.fromDate ? formatDate(header.fromDate) : '—';
  const toDateLabel = header.toDate ? formatDate(header.toDate) : '—';
  const individualLabel = header.individualName
    ? sanitizeText(header.individualName)
    : '—';
  const memberStatusLabel = header.memberStatus ? sanitizeText(header.memberStatus) : '—';
  const totalValueLabel = formatCurrency(header.totalValue ?? null);
  const { headers: tableHeaders, rows: tableRows } = buildItemsTable(items, pl.label, pl.lower, ieKitLabel);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Cleared ${pl.labelPlural} Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .meta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 12px;
      }

      .meta-block {
        min-width: 220px;
      }

      .meta-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .meta-value {
        font-size: 14px;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Cleared ${pl.labelPlural} Report</h1>
      <div class="meta-row">
        <div class="meta-block">
          <div class="meta-label">From</div>
          <div class="meta-value">${fromDateLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">To</div>
          <div class="meta-value">${toDateLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">${pl.label}</div>
          <div class="meta-value">${individualLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">Cleared / Active</div>
          <div class="meta-value">${memberStatusLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">Total Value</div>
          <div class="meta-value">${totalValueLabel}</div>
        </div>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            ${tableHeaders}
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
